import { useMemo } from "react";
import { FaRegEdit } from "react-icons/fa";
import { IHomeTileOption } from "@/api_framework/api_modals/HomeTileConfig";
import TableComponent, { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import StructuredTableSkeleton from "@/app/components/ui/Skeletons/StructuredTableSkeleton";
import { useUtils } from "@/app/hooks/useUtils";
import { StatusBadge, TileAvatar } from "../components";
import {
  RIBBON_TYPE_FILTERS,
  RibbonTypeFilter,
  TILE_TYPE_LABEL,
  getTileVisibilityTitle,
  secondaryButtonClassName,
  tileTypeChipClassName,
} from "../interface";

const EmptyCell = () => <span className="text-xs text-slate-400">—</span>;

const DynamicStatusCell = ({ tile }: { tile: IHomeTileOption }) => (
  <span
    className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
      tile.is_dynamic_enabled
        ? "border-[#193A6A]/20 bg-[#EEF5FF] text-[#193A6A]"
        : "border-slate-200 bg-slate-50 text-slate-500"
    }`}
  >
    {tile.is_dynamic_enabled ? "On" : "Off"}
  </span>
);

const RIBBON_TYPE_COLUMN_FILTER_OPTIONS = RIBBON_TYPE_FILTERS.map(({ key, label }) => ({ value: key, label }));

const useTileColumns = ({
  onSelect,
  ribbonTypeFilter,
  onRibbonTypeFilterChange,
}: {
  onSelect: (tileId: string) => void;
  ribbonTypeFilter: RibbonTypeFilter;
  onRibbonTypeFilterChange: (next: RibbonTypeFilter) => void;
}): TableColumn<IHomeTileOption>[] => {
  const { veticDateFormat } = useUtils();

  return useMemo<TableColumn<IHomeTileOption>[]>(
    () => [
      {
        id: "serial_number",
        header: "S.No",
        width: 70,
        cell: ({ rowIndex }) => <span className="text-xs tabular-nums text-slate-600">{rowIndex + 1}</span>,
        headerClassName: "text-slate-500",
      },
      {
        id: "image",
        header: "Tile Image",
        width: 100,
        cell: ({ row }) => <TileAvatar name={row.tile_name} imageUrl={row.image_url} className="h-10 w-10 p-1.5" />,
        headerClassName: "text-slate-500",
      },
      {
        id: "tile_name",
        header: "Tile Name",
        minWidth: 200,
        cell: ({ row }) => <span className="text-xs font-semibold text-slate-800">{row.tile_name || "—"}</span>,
        headerClassName: "text-slate-500",
      },
      {
        id: "tile_type",
        header: "Type",
        width: 110,
        cell: ({ row }) => <span className={tileTypeChipClassName}>{TILE_TYPE_LABEL[row.tile_type]}</span>,
        headerClassName: "text-slate-500",
      },
      {
        id: "dynamic_status",
        align: "center",
        header: "Dynamic Status",
        overflow: "visible",
        filter: {
          options: RIBBON_TYPE_COLUMN_FILTER_OPTIONS,
          value: ribbonTypeFilter,
          onChange: (next) => onRibbonTypeFilterChange(next as RibbonTypeFilter),
        },
        width: 170,
        cell: ({ row }) => <DynamicStatusCell tile={row} />,
        headerClassName: "text-slate-500",
      },
      {
        id: "tile_status",
        header: "Tile Status",
        width: 130,
        cell: ({ row }) => (
          <StatusBadge status={row.visible ? "ACTIVE" : "INACTIVE"} title={getTileVisibilityTitle(row.visible)} />
        ),
        headerClassName: "text-slate-500",
      },
      {
        id: "rule_count",
        align: "center",
        header: (
          <>
            Number of
            <br />
            Active Rules
          </>
        ),
        width: 140,
        cell: ({ row }) => (
          <span className="text-xs font-semibold tabular-nums text-slate-700" title="Active rules on this tile">
            {row.active_rule_count}
          </span>
        ),
        headerClassName: "text-slate-500",
      },
      {
        id: "updated_at",
        header: "Updated At",
        width: 170,
        cell: ({ row }) => {
          const updatedAt = veticDateFormat(row.updated_at);
          return updatedAt ? (
            <span className="whitespace-nowrap text-xs text-slate-600">{updatedAt}</span>
          ) : (
            <EmptyCell />
          );
        },
        headerClassName: "text-slate-500",
      },
      {
        id: "action",
        header: "Edit",
        align: "center",
        width: 110,
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => onSelect(row.tile_id)}
            className="rounded-lg border border-transparent p-2 text-[#193A6A] transition-colors hover:border-slate-200 hover:bg-slate-50"
            aria-label={`Edit ${row.tile_name}`}
            title="Edit tile"
          >
            <FaRegEdit className="h-4 w-4" />
          </button>
        ),
        headerClassName: "text-slate-500 text-center",
      },
    ],
    [onSelect, ribbonTypeFilter, onRibbonTypeFilterChange, veticDateFormat],
  );
};

const TileTableMessage = ({
  message,
  actionLabel,
  onAction,
}: {
  message: string;
  actionLabel: string;
  onAction: () => void;
}) => (
  <div className="mx-5 my-4 rounded-xl border border-dashed border-slate-200 bg-slate-50/70 px-5 py-10 text-center">
    <p className="text-xs text-slate-500">{message}</p>
    <button type="button" onClick={onAction} className={`mt-3 ${secondaryButtonClassName}`}>
      {actionLabel}
    </button>
  </div>
);

export const TileTable = ({
  tiles,
  totalCount,
  isLoading,
  ribbonTypeFilter,
  onRibbonTypeFilterChange,
  onSelect,
  onShowAll,
  onRetry,
}: {
  tiles: IHomeTileOption[];
  totalCount: number;
  isLoading: boolean;
  ribbonTypeFilter: RibbonTypeFilter;
  onRibbonTypeFilterChange: (next: RibbonTypeFilter) => void;
  onSelect: (tileId: string) => void;
  onShowAll: () => void;
  onRetry: () => void;
}) => {
  const columns = useTileColumns({ onSelect, ribbonTypeFilter, onRibbonTypeFilterChange });

  if (isLoading) {
    return (
      <div className="px-5 py-4">
        <StructuredTableSkeleton columns={columns.length} />
      </div>
    );
  }

  if (tiles.length > 0) {
    return (
      <TableComponent<IHomeTileOption>
        columns={columns}
        data={tiles}
        getRowId={(row) => row.tile_id}
        onRowClick={(row) => onSelect(row.tile_id)}
        rowClassName={() => "cursor-pointer hover:bg-slate-50"}
        containerClassName="!mr-0 !rounded-none !border-0 bg-white"
        scrollContainerClassName="overflow-x-auto max-h-[calc(100vh-260px)]"
        minTableWidthClassName="min-w-full"
        tableClassName="min-w-full divide-y divide-slate-100"
        stickyHeader
        wrapCells
      />
    );
  }

  if (totalCount > 0) {
    return (
      <TileTableMessage
        message={
          ribbonTypeFilter === "DYNAMIC" ? "No tile has dynamic status on." : "Every tile has dynamic status on."
        }
        actionLabel="Show all tiles"
        onAction={onShowAll}
      />
    );
  }

  return (
    <TileTableMessage message="No home tiles are available to configure." actionLabel="Retry" onAction={onRetry} />
  );
};
